/* eslint-disable unicode-bom, no-unused-vars, react-hooks/exhaustive-deps */

// ============================================================================
// Employees.js — ISHMAI Admin Panel
// ----------------------------------------------------------------------------
// Reuses the EXISTING Roles & Permissions system.
// The Role dropdown is populated live from getAllRoles() (GET /api/admin/roles).
// Employee APIs used:
//   - GET /api/admin/employees?page=1&limit=10&search=&roleId=&status=
//   - GET /api/admin/employees/:id
//   - POST /api/admin/employees
//   - PUT /api/admin/employees/:id
//   - DELETE /api/admin/employees/:id
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import DeleteSuccessModal from '../components/DeleteSuccessModal';
import {
  getAllRoles,
  getEmployees,
  getEmployeeById,
  createEmployee,
  updateEmployee,
  deleteEmployee,
} from '../services/api';
import './employe.css';

var DEFAULT_DEPARTMENTS = [
  'Sales',
  'Operations',
  'Senior Operations',
  'Content & Operations',
  'Warehouse',
  'Marketing',
  'Finance',
  'Customer Support',
  'IT / Admin',
];

var STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

var AVATAR_PALETTE = ['#6D28D9', '#0ea5e9', '#d99a2b', '#16a34a', '#ec4899', '#f59e0b', '#a855f7'];

var initials = function (name) {
  var parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  var first = parts[0][0] || '';
  var second = parts.length > 1 ? (parts[parts.length - 1][0] || '') : '';
  return (first + second).toUpperCase();
};

var avatarColor = function (name) {
  var str = String(name || '');
  var hash = 0;
  for (var i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

var formatDate = function (d) {
  if (!d) return '\u2014';
  var dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

var isFullAccessRole = function (role) {
  if (!role) return false;
  if (role.allAccess === true || role.fullAccess === true) return true;
  return String(role.name || '').toLowerCase() === 'super admin' && role.isSystemRole !== false;
};

var rolePermissionLabel = function (role) {
  if (isFullAccessRole(role)) return 'All Access';
  if (Array.isArray(role.permissions)) return role.permissions.length + ' Permissions';
  if (typeof role.moduleCount === 'number') return role.moduleCount + ' Permissions';
  return '';
};

var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var NAME_PATTERN = /^[a-zA-Z\s]+$/;

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

var Icon = function (props) {
  var children = props.children; var size = props.size || 15;
  return React.createElement('svg', {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
  }, children);
};

var UsersIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('path', { d: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' }),
    React.createElement('circle', { cx: 9, cy: 7, r: 4 }),
    React.createElement('path', { d: 'M23 21v-2a4 4 0 0 0-3-3.87' }),
    React.createElement('path', { d: 'M16 3.13a4 4 0 0 1 0 7.75' })
  );
};

var SearchIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('circle', { cx: 11, cy: 11, r: 8 }),
    React.createElement('line', { x1: 21, y1: 21, x2: 16.65, y2: 16.65 })
  );
};

var EyeIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }),
    React.createElement('circle', { cx: 12, cy: 12, r: 3 })
  );
};

var EyeOffIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('path', { d: 'M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19' }),
    React.createElement('path', { d: 'M14.12 14.12a3 3 0 1 1-4.24-4.24' }),
    React.createElement('line', { x1: 1, y1: 1, x2: 23, y2: 23 })
  );
};

var PencilIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('path', { d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }),
    React.createElement('path', { d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' })
  );
};

var TrashIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('polyline', { points: '3 6 5 6 21 6' }),
    React.createElement('path', { d: 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' }),
    React.createElement('path', { d: 'M10 11v6M14 11v6' })
  );
};

var XIcon = function () {
  return React.createElement(Icon, { size: 16 },
    React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
};

var DownloadIcon = function () {
  return React.createElement(Icon, null,
    React.createElement('path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }),
    React.createElement('polyline', { points: '7 10 12 15 17 10' }),
    React.createElement('line', { x1: 12, y1: 15, x2: 12, y2: 3 })
  );
};

var ChevronIcon = function () {
  return React.createElement('span', { className: 'emp-caret' }, '\u25BC');
};

// ---------------------------------------------------------------------------
// Custom Select (Opens cleanly over table without overlapping issues)
// ---------------------------------------------------------------------------

var CustomSelect = function (props) {
  var value = props.value; var onChange = props.onChange; var options = props.options;
  var placeholder = props.placeholder || 'Select';
  var disabled = props.disabled;
  var os = useState(false); var open = os[0]; var setOpen = os[1];
  var ref = useRef();

  useEffect(function () {
    var handler = function (e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, []);

  var selected = options.find(function (o) { return String(o.value) === String(value); });

  return React.createElement('div', {
    ref: ref,
    className: 'emp-select' + (disabled ? ' is-disabled' : '') + (open ? ' is-open' : ''),
  },
    React.createElement('button', {
      type: 'button',
      className: 'emp-select-trigger',
      disabled: disabled,
      onClick: function () { if (!disabled) setOpen(!open); },
    },
      React.createElement('span', null, selected ? selected.label : placeholder),
      React.createElement(ChevronIcon, null)
    ),
    open && !disabled && React.createElement('div', { className: 'emp-select-dd' },
      options.length === 0
        ? React.createElement('div', { className: 'emp-select-empty' }, 'No options available')
        : options.map(function (o) {
            var isSel = String(o.value) === String(value);
            return React.createElement('div', {
              key: String(o.value),
              className: 'emp-select-opt' + (isSel ? ' is-sel' : ''),
              onClick: function () { onChange(o.value); setOpen(false); },
            },
              React.createElement('span', null, o.label),
              o.badge && React.createElement('span', { className: 'emp-select-badge' }, o.badge)
            );
          })
    )
  );
};

// ---------------------------------------------------------------------------
// View employee modal (Generous button spacing away from bottom border)
// ---------------------------------------------------------------------------

var ViewEmployeeModal = function (props) {
  var employee = props.employee; var onClose = props.onClose;
  if (!employee) return null;

  var name = employee.fullName || employee.name || '\u2014';
  var email = employee.workEmail || employee.email || '\u2014';
  var roleName = (employee.role && employee.role.name) || employee.roleName || '\u2014';

  return React.createElement('div', {
    className: 'modal-overlay',
    onClick: function (e) { if (e.target.classList.contains('modal-overlay')) onClose(); },
  },
    React.createElement('div', { className: 'view-modal-box emp-view-box' },
      React.createElement('div', { className: 'modal-hdr' },
        React.createElement('span', { className: 'modal-title' }, 'Employee Details'),
        React.createElement('button', { className: 'modal-close', type: 'button', onClick: onClose }, React.createElement(XIcon, null))
      ),
      React.createElement('div', { className: 'emp-view-avatar-row' },
        React.createElement('div', { className: 'emp-avatar emp-avatar-lg', style: { background: avatarColor(name) } }, initials(name)),
        React.createElement('div', null,
          React.createElement('div', { className: 'emp-view-name' }, name),
          React.createElement('div', { className: 'emp-view-email' }, email)
        )
      ),
      React.createElement('div', { className: 'view-row' },
        React.createElement('span', { className: 'view-label' }, 'Phone'),
        React.createElement('span', { className: 'view-val' }, employee.phone || '\u2014')
      ),
      React.createElement('div', { className: 'view-row' },
        React.createElement('span', { className: 'view-label' }, 'Role'),
        React.createElement('span', { className: 'view-val' }, roleName)
      ),
      React.createElement('div', { className: 'view-row' },
        React.createElement('span', { className: 'view-label' }, 'Department'),
        React.createElement('span', { className: 'view-val' }, employee.department || '\u2014')
      ),
      React.createElement('div', { className: 'view-row' },
        React.createElement('span', { className: 'view-label' }, 'Status'),
        React.createElement('span', { className: 'bdg ' + (String(employee.status).toLowerCase() === 'inactive' ? 'bdg-b' : 'bdg-g') }, employee.status || 'active')
      ),
      React.createElement('div', { className: 'view-row' },
        React.createElement('span', { className: 'view-label' }, 'Added On'),
        React.createElement('span', { className: 'view-val' }, formatDate(employee.createdAt))
      ),
      React.createElement('p', { className: 'emp-view-note' }, 'Module access for this employee is controlled by their assigned Role permissions.'),
      React.createElement('div', { className: 'fa', style: { marginTop: '28px', paddingBottom: '4px', display: 'flex', justifyContent: 'flex-end' } },
        React.createElement('button', { className: 'btn btn-g', type: 'button', onClick: onClose }, 'Close')
      )
    )
  );
};

// ---------------------------------------------------------------------------
// Add / Edit Employee modal with comprehensive validation & button spacing
// ---------------------------------------------------------------------------

var EMPTY_FORM = {
  fullName: '', workEmail: '', phone: '', password: '', roleId: '', department: '', status: 'active',
};

var EmployeeFormModal = function (props) {
  var mode = props.mode; // 'add' | 'edit'
  var initialData = props.initialData;
  var roles = props.roles;
  var onClose = props.onClose;
  var onSaved = props.onSaved;

  var getInitialForm = function () {
    if (!initialData) return Object.assign({}, EMPTY_FORM);
    return {
      fullName: initialData.fullName || initialData.name || '',
      workEmail: initialData.workEmail || initialData.email || '',
      phone: initialData.phone || '',
      password: '',
      roleId: initialData.roleId || (initialData.role && (initialData.role.id || initialData.role._id)) || '',
      department: initialData.department || '',
      status: initialData.status || 'active',
    };
  };

  var formState = useState(getInitialForm);
  var form = formState[0]; var setForm = formState[1];
  var errorsState = useState({}); var errors = errorsState[0]; var setErrors = errorsState[1];
  var showPwdState = useState(false); var showPwd = showPwdState[0]; var setShowPwd = showPwdState[1];
  var savingState = useState(false); var saving = savingState[0]; var setSaving = savingState[1];
  var submitErrorState = useState(''); var submitError = submitErrorState[0]; var setSubmitError = submitErrorState[1];

  var selectedRole = roles.find(function (r) { return String(r._id || r.id) === String(form.roleId); });
  var departmentOptions = useMemo(function () {
    var list = (selectedRole && Array.isArray(selectedRole.departments) && selectedRole.departments.length)
      ? selectedRole.departments
      : DEFAULT_DEPARTMENTS;
    return list.map(function (d) { return { value: d, label: d }; });
  }, [selectedRole]);

  var setField = function (field, value) {
    setForm(function (prev) { var next = Object.assign({}, prev); next[field] = value; return next; });
    if (errors[field]) setErrors(function (prev) { var next = Object.assign({}, prev); delete next[field]; return next; });
  };

  var handleFullNameChange = function (e) {
    // Only allow letters and spaces
    var filtered = e.target.value.replace(/[^a-zA-Z\s]/g, '');
    setField('fullName', filtered);
  };

  var handlePhoneChange = function (e) {
    // Only allow digits, max 12 digits
    var filtered = e.target.value.replace(/[^0-9]/g, '').slice(0, 12);
    setField('phone', filtered);
  };

  var validate = function () {
    var e = {};
    var trimmedName = form.fullName.trim();
    if (!trimmedName) {
      e.fullName = 'Full name is required';
    } else if (!NAME_PATTERN.test(trimmedName)) {
      e.fullName = 'Full name can only contain letters and spaces';
    } else if (trimmedName.length < 2) {
      e.fullName = 'Full name must be at least 2 characters';
    }

    var trimmedEmail = form.workEmail.trim();
    if (!trimmedEmail) {
      e.workEmail = 'Work email is required';
    } else if (!EMAIL_PATTERN.test(trimmedEmail)) {
      e.workEmail = 'Please enter a valid email address (e.g. name@ishmai.com)';
    }

    var trimmedPhone = form.phone.trim();
    if (!trimmedPhone) {
      e.phone = 'Phone number is required';
    } else if (trimmedPhone.length < 10) {
      e.phone = 'Phone number must be at least 10 digits';
    }

    if (mode === 'add') {
      if (!form.password) {
        e.password = 'Password is required';
      } else if (form.password.length < 6) {
        e.password = 'Password must be at least 6 characters';
      }
    } else if (form.password && form.password.length < 6) {
      e.password = 'Password must be at least 6 characters';
    }

    if (!form.roleId) {
      e.roleId = 'Please select a role';
    }
    if (!form.status) {
      e.status = 'Please select a status';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  var handleSubmit = function () {
    if (!validate()) return;
    setSaving(true);
    setSubmitError('');

    var targetId = initialData ? (initialData.id || initialData._id) : null;

    var payload = {
      fullName: form.fullName.trim(),
      workEmail: form.workEmail.trim().toLowerCase(),
      phone: form.phone.trim(),
      roleId: form.roleId,
      department: form.department || undefined,
      status: form.status,
    };

    if (mode === 'edit') {
      payload = {
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        department: form.department || undefined,
        status: form.status,
        roleId: form.roleId,
      };
      if (form.password) payload.password = form.password;
    } else {
      payload.password = form.password;
    }

    var request = mode === 'edit' ? updateEmployee(targetId, payload) : createEmployee(payload);

    request
      .then(function (res) {
        onSaved((res && res.data && res.data.data) || (res && res.data) || payload);
      })
      .catch(function (err) {
        var msg = (err.response && err.response.data && (err.response.data.message || err.response.data.msg || err.response.data.error)) || 'Failed to save employee';
        setSubmitError(msg);
        console.error('Save employee error:', (err.response && err.response.data) || err.message);
      })
      .finally(function () { setSaving(false); });
  };

  return React.createElement('div', {
    className: 'modal-overlay',
    onClick: function (e) { if (e.target.classList.contains('modal-overlay')) onClose(); },
  },
    React.createElement('div', { className: 'emp-form-modal' },
      React.createElement('div', { className: 'modal-hdr' },
        React.createElement('span', { className: 'modal-title' }, mode === 'edit' ? 'Edit Employee' : 'Add Employee'),
        React.createElement('button', { className: 'modal-close', type: 'button', onClick: onClose, disabled: saving }, React.createElement(XIcon, null))
      ),

      submitError && React.createElement('div', { className: 'mbx mbx-e' }, submitError),

      React.createElement('div', { className: 'emp-form-grid' },
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Full Name ', React.createElement('span', { className: 'emp-req' }, '*')),
          React.createElement('input', {
            placeholder: 'e.g. John Doe (letters only)',
            value: form.fullName,
            onChange: handleFullNameChange,
            style: errors.fullName ? { borderColor: '#ef4444' } : null,
          }),
          errors.fullName && React.createElement('span', { className: 'emp-field-error' }, errors.fullName)
        ),
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Work Email ', React.createElement('span', { className: 'emp-req' }, '*')),
          React.createElement('input', {
            type: 'email',
            placeholder: 'name@ishmai.com',
            value: form.workEmail,
            disabled: mode === 'edit',
            onChange: function (e) { setField('workEmail', e.target.value); },
            style: errors.workEmail ? { borderColor: '#ef4444' } : null,
          }),
          errors.workEmail && React.createElement('span', { className: 'emp-field-error' }, errors.workEmail)
        ),
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Phone (10 digits) ', React.createElement('span', { className: 'emp-req' }, '*')),
          React.createElement('input', {
            placeholder: '9876543210',
            value: form.phone,
            onChange: handlePhoneChange,
            style: errors.phone ? { borderColor: '#ef4444' } : null,
          }),
          errors.phone && React.createElement('span', { className: 'emp-field-error' }, errors.phone)
        ),
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null,
            mode === 'edit' ? 'New Password' : 'Password ',
            mode !== 'edit' && React.createElement('span', { className: 'emp-req' }, '*')
          ),
          React.createElement('div', { className: 'emp-pwd-field' },
            React.createElement('input', {
              type: showPwd ? 'text' : 'password',
              placeholder: mode === 'edit' ? 'Leave blank to keep current password' : 'Create a password (min 6 chars)',
              value: form.password,
              onChange: function (e) { setField('password', e.target.value); },
              style: errors.password ? { borderColor: '#ef4444' } : null,
            }),
            React.createElement('button', {
              type: 'button',
              className: 'emp-pwd-toggle',
              onClick: function () { setShowPwd(!showPwd); },
              'aria-label': showPwd ? 'Hide password' : 'Show password',
            }, showPwd ? React.createElement(EyeOffIcon, null) : React.createElement(EyeIcon, null))
          ),
          errors.password && React.createElement('span', { className: 'emp-field-error' }, errors.password)
        ),
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Role ', React.createElement('span', { className: 'emp-req' }, '*')),
          React.createElement(CustomSelect, {
            value: form.roleId,
            onChange: function (v) { setField('roleId', v); },
            placeholder: 'Select Role',
            options: roles.map(function (r) {
              return { value: String(r._id || r.id), label: r.name, badge: rolePermissionLabel(r) };
            }),
          }),
          errors.roleId && React.createElement('span', { className: 'emp-field-error' }, errors.roleId)
        ),
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Department'),
          React.createElement(CustomSelect, {
            value: form.department,
            onChange: function (v) { setField('department', v); },
            placeholder: 'Select Department',
            options: departmentOptions,
          })
        ),
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Status ', React.createElement('span', { className: 'emp-req' }, '*')),
          React.createElement(CustomSelect, {
            value: form.status,
            onChange: function (v) { setField('status', v); },
            options: STATUS_OPTIONS,
          }),
          errors.status && React.createElement('span', { className: 'emp-field-error' }, errors.status)
        )
      ),

      React.createElement('div', {
        className: 'fa',
        style: { marginTop: '32px', paddingBottom: '6px', display: 'flex', justifyContent: 'flex-end', gap: '14px' }
      },
        React.createElement('button', {
          className: 'btn btn-g',
          type: 'button',
          onClick: onClose,
          disabled: saving
        }, 'Cancel'),
        React.createElement('button', {
          className: 'btn btn-p',
          type: 'button',
          disabled: saving,
          style: { opacity: saving ? 0.7 : 1 },
          onClick: handleSubmit,
        }, saving ? 'Saving...' : (mode === 'edit' ? 'Save Changes' : 'Add Employee'))
      )
    )
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

var Employees = function () {
  var employeesState = useState([]); var employees = employeesState[0]; var setEmployees = employeesState[1];
  var totalState = useState(0); var total = totalState[0]; var setTotal = totalState[1];
  var pageState = useState(1); var page = pageState[0]; var setPage = pageState[1];
  var pagesState = useState(1); var pages = pagesState[0]; var setPages = pagesState[1];

  var loadingState = useState(true); var loading = loadingState[0]; var setLoading = loadingState[1];
  var errorState = useState(''); var error = errorState[0]; var setError = errorState[1];

  var searchState = useState(''); var search = searchState[0]; var setSearch = searchState[1];
  var searchDebState = useState(''); var searchDeb = searchDebState[0]; var setSearchDeb = searchDebState[1];
  var roleFilterState = useState(''); var roleFilter = roleFilterState[0]; var setRoleFilter = roleFilterState[1];
  var statusFilterState = useState(''); var statusFilter = statusFilterState[0]; var setStatusFilter = statusFilterState[1];

  var rolesState = useState([]); var roles = rolesState[0]; var setRoles = rolesState[1];
  var rolesLoadingState = useState(true); var rolesLoading = rolesLoadingState[0]; var setRolesLoading = rolesLoadingState[1];

  var formModalState = useState(null); var formModal = formModalState[0]; var setFormModal = formModalState[1];
  var viewEmpState = useState(null); var viewEmp = viewEmpState[0]; var setViewEmp = viewEmpState[1];
  var deleteTargetState = useState(null); var deleteTarget = deleteTargetState[0]; var setDeleteTarget = deleteTargetState[1];
  var deleteSuccessState = useState(null); var deleteSuccess = deleteSuccessState[0]; var setDeleteSuccess = deleteSuccessState[1];
  var flashState = useState({ text: '', type: '' }); var flash = flashState[0]; var setFlash = flashState[1];

  var flashMsg = function (text, type) {
    setFlash({ text: text, type: type });
    window.setTimeout(function () { setFlash({ text: '', type: '' }); }, 5000);
  };

  // -- Load roles dynamically from Roles & Permissions API --
  var fetchRoles = useCallback(function () {
    setRolesLoading(true);
    getAllRoles()
      .then(function (res) {
        var d = res.data;
        var list = (d && d.data) ? d.data : (Array.isArray(d) ? d : []);
        setRoles(list);
      })
      .catch(function (e) {
        console.error('Failed to load roles for employee form:', e);
        setRoles([]);
      })
      .finally(function () { setRolesLoading(false); });
  }, []);

  useEffect(function () { fetchRoles(); }, [fetchRoles]);

  // -- Debounce search --
  useEffect(function () {
    var t = setTimeout(function () { setPage(1); setSearchDeb(search); }, 400);
    return function () { clearTimeout(t); };
  }, [search]);

  // -- Load employees --
  var fetchEmployees = useCallback(function () {
    setLoading(true);
    setError('');
    getEmployees({
      search: searchDeb || undefined,
      roleId: roleFilter || undefined,
      status: statusFilter || undefined,
      page: page,
      limit: 10,
    })
      .then(function (res) {
        var resData = res && res.data ? res.data : {};
        var list = Array.isArray(resData.data)
          ? resData.data
          : (resData.data && Array.isArray(resData.data.list) ? resData.data.list : (resData.list ? resData.list : (Array.isArray(resData) ? resData : [])));
        var pag = resData.pagination || (resData.data && resData.data.pagination) || {};
        var tot = pag.total !== undefined ? pag.total : (resData.total !== undefined ? resData.total : list.length);
        var pgs = pag.pages !== undefined ? pag.pages : (resData.pages !== undefined ? resData.pages : 1);

        setEmployees(list);
        setTotal(tot);
        setPages(pgs || 1);
      })
      .catch(function (e) {
        console.error('Failed to load employees:', e);
        setError('Failed to load employees. Please try again.');
        setEmployees([]);
      })
      .finally(function () { setLoading(false); });
  }, [searchDeb, roleFilter, statusFilter, page]);

  useEffect(function () { fetchEmployees(); }, [fetchEmployees]);

  var getEmployeeRoleName = function (emp) {
    if (emp.role && emp.role.name) return emp.role.name;
    if (emp.roleName) return emp.roleName;
    var targetRoleId = emp.roleId || (emp.role && (emp.role.id || emp.role._id));
    var r = roles.find(function (r) { return String(r._id || r.id) === String(targetRoleId); });
    return r ? r.name : '\u2014';
  };

  var handleSaved = function () {
    var modeText = formModal ? formModal.mode : 'add';
    setFormModal(null);
    flashMsg(modeText === 'edit' ? 'Employee updated successfully!' : 'Employee created successfully!', 's');
    fetchEmployees();
  };

  var handleViewClick = function (emp) {
    var empId = emp.id || emp._id;
    setViewEmp(emp);
    getEmployeeById(empId)
      .then(function (res) {
        var full = (res && res.data && res.data.data) ? res.data.data : (res && res.data) || emp;
        setViewEmp(full);
      })
      .catch(function (e) { console.error('Failed to load employee detail:', e); });
  };

  var handleDeleteConfirmed = function () {
    var emp = deleteTarget;
    var empId = emp.id || emp._id;
    setDeleteTarget(null);
    deleteEmployee(empId)
      .then(function () {
        setDeleteSuccess({ id: empId, name: emp.fullName || emp.name });
      })
      .catch(function (e) {
        var msg = (e.response && e.response.data && (e.response.data.message || e.response.data.msg)) || 'Failed to delete employee';
        flashMsg(msg, 'e');
        console.error('Delete employee error:', (e.response && e.response.data) || e.message);
      });
  };

  var handleDeleteSuccessClose = function () {
    var deletedId = deleteSuccess && deleteSuccess.id;
    setDeleteSuccess(null);
    if (deletedId) {
      setEmployees(function (prev) {
        return prev.filter(function (e) { return (e.id || e._id) !== deletedId; });
      });
    }
    fetchEmployees();
  };

  // Export employee data to CSV
  var handleExportEmployees = function () {
    if (!employees || employees.length === 0) {
      flashMsg('No employee records available to export.', 'e');
      return;
    }
    var headers = ['Employee Name', 'Work Email', 'Phone', 'Role', 'Department', 'Status', 'Added On'];
    var rows = employees.map(function (emp) {
      var name = emp.fullName || emp.name || '';
      var email = emp.workEmail || emp.email || '';
      var phone = emp.phone || '';
      var role = getEmployeeRoleName(emp) || '';
      var dept = emp.department || '';
      var status = emp.status || 'active';
      var date = formatDate(emp.createdAt);
      return [name, email, phone, role, dept, status, date];
    });

    var csv = [headers].concat(rows).map(function (r) {
      return r.map(function (cell) {
        return '"' + String(cell || '').replace(/"/g, '""') + '"';
      }).join(',');
    }).join('\n');

    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url;
    link.download = 'employees_' + new Date().toISOString().slice(0, 10) + '.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    flashMsg('Employees exported successfully!', 's');
  };

  var roleFilterOptions = [{ value: '', label: 'All Roles' }].concat(
    roles.map(function (r) { return { value: String(r._id || r.id), label: r.name }; })
  );
  var statusFilterOptions = [{ value: '', label: 'All Status' }].concat(STATUS_OPTIONS);

  return React.createElement(AdminLayout, { hideLayoutOnModal: !!(formModal || viewEmp || deleteTarget || deleteSuccess) },

    formModal && React.createElement(EmployeeFormModal, {
      mode: formModal.mode,
      initialData: formModal.data,
      roles: roles,
      onClose: function () { setFormModal(null); },
      onSaved: handleSaved,
    }),

    viewEmp && React.createElement(ViewEmployeeModal, { employee: viewEmp, onClose: function () { setViewEmp(null); } }),

    deleteTarget && React.createElement(DeleteConfirmModal, {
      itemName: deleteTarget.fullName || deleteTarget.name,
      message: 'Are you sure you want to delete this employee?',
      onConfirm: handleDeleteConfirmed,
      onCancel: function () { setDeleteTarget(null); },
    }),

    deleteSuccess && React.createElement(DeleteSuccessModal, {
      isOpen: true,
      message: 'Deleted Successfully',
      subtitle: 'The employee has been deleted successfully.',
      onClose: handleDeleteSuccessClose,
    }),

    // -- Header --
    React.createElement('div', { className: 'page-header-row' },
      React.createElement('div', { className: 'page-header-main' },
        React.createElement('div', { className: 'page-header-title-row' },
          React.createElement('div', { className: 'page-header-icon' }, React.createElement(UsersIcon, { size: 18 })),
          React.createElement('div', { className: 'ptitle' }, 'Employees')
        ),
        React.createElement('div', { className: 'header-subtitle' }, 'Manage your employees and their access')
      ),
      React.createElement('div', { className: 'emp-header-actions' },
        React.createElement('button', {
          className: 'btn btn-g emp-export-btn',
          type: 'button',
          onClick: handleExportEmployees,
          title: 'Export employees list to CSV',
        },
          React.createElement(DownloadIcon, null), ' Export'
        ),
        React.createElement('button', {
          className: 'add-btn', type: 'button',
          onClick: function () { setFormModal({ mode: 'add', data: null }); },
        },
          React.createElement('svg', { viewBox: '0 0 24 24', style: { width: 15, height: 15, fill: '#fff' } },
            React.createElement('path', { d: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' })
          ),
          ' Add Employee'
        )
      )
    ),

    flash.text && React.createElement('div', { className: 'mbx ' + (flash.type === 'e' ? 'mbx-e' : 'mbx-s') }, flash.text),
    error && React.createElement('div', { className: 'rp-error-banner' }, error),

    // -- Toolbar with clean z-index hierarchy --
    React.createElement('div', { className: 'emp-toolbar' },
      React.createElement('div', { className: 'emp-search-field' },
        React.createElement(SearchIcon, null),
        React.createElement('input', {
          placeholder: 'Search by name, email or phone\u2026', value: search,
          onChange: function (e) { setSearch(e.target.value); },
        })
      ),
      React.createElement('div', { className: 'emp-toolbar-filters' },
        React.createElement(CustomSelect, {
          value: roleFilter, onChange: function (v) { setPage(1); setRoleFilter(v); }, options: roleFilterOptions,
        }),
        React.createElement(CustomSelect, {
          value: statusFilter, onChange: function (v) { setPage(1); setStatusFilter(v); }, options: statusFilterOptions,
        })
      )
    ),

    // -- Table --
    React.createElement('div', { className: 'wid' },
      React.createElement('div', { className: 'wh' },
        React.createElement('span', { className: 'wt' }, 'All Employees (', total, ')')
      ),

      React.createElement('table', { className: 'tbl' },
        React.createElement('thead', null,
          React.createElement('tr', null,
            React.createElement('th', null, 'EMPLOYEE'),
            React.createElement('th', null, 'PHONE'),
            React.createElement('th', null, 'ROLE'),
            React.createElement('th', null, 'DEPARTMENT'),
            React.createElement('th', null, 'STATUS'),
            React.createElement('th', null, 'ACTIONS')
          )
        ),
        React.createElement('tbody', null,
          loading
            ? React.createElement('tr', null, React.createElement('td', { colSpan: 6, style: { textAlign: 'center', color: '#94a3b8', padding: 32 } }, 'Loading employees...'))
            : employees.length === 0
              ? React.createElement('tr', null, React.createElement('td', { colSpan: 6, style: { textAlign: 'center', color: '#94a3b8', padding: 32 } }, 'No employees found.'))
              : employees.map(function (emp, idx) {
                  var name = emp.fullName || emp.name || '\u2014';
                  var email = emp.workEmail || emp.email || '\u2014';
                  return React.createElement('tr', { key: emp.id || emp._id || idx },
                    React.createElement('td', null,
                      React.createElement('div', { className: 'emp-row-cell' },
                        React.createElement('div', { className: 'emp-avatar', style: { background: avatarColor(name) } }, initials(name)),
                        React.createElement('div', null,
                          React.createElement('div', { className: 'emp-row-name' }, name),
                          React.createElement('div', { className: 'emp-row-email' }, email)
                        )
                      )
                    ),
                    React.createElement('td', { style: { color: '#0f172a', fontSize: 13, fontWeight: 600 } }, emp.phone || '\u2014'),
                    React.createElement('td', { style: { fontWeight: 600 } }, getEmployeeRoleName(emp)),
                    React.createElement('td', { style: { color: '#475569', fontSize: 13 } }, emp.department || '\u2014'),
                    React.createElement('td', null,
                      React.createElement('span', { className: 'bdg ' + (String(emp.status).toLowerCase() === 'inactive' ? 'bdg-b' : 'bdg-g') }, emp.status || 'active')
                    ),
                    React.createElement('td', { className: 'actions-cell' },
                      React.createElement('div', { className: 'row-actions' },
                        React.createElement('button', {
                          className: 'row-action-btn row-action-view', title: 'View Details', 'aria-label': 'View', type: 'button',
                          onClick: function () { handleViewClick(emp); },
                        }, React.createElement(EyeIcon, null)),
                        React.createElement('button', {
                          className: 'row-action-btn row-action-edit', title: 'Edit Employee', 'aria-label': 'Edit', type: 'button',
                          onClick: function () { setFormModal({ mode: 'edit', data: emp }); },
                        }, React.createElement(PencilIcon, null)),
                        React.createElement('button', {
                          className: 'row-action-btn row-action-delete', title: 'Delete Employee', 'aria-label': 'Delete', type: 'button',
                          onClick: function () { setDeleteTarget(emp); },
                        }, React.createElement(TrashIcon, null))
                      )
                    )
                  );
                })
        )
      ),

      !loading && employees.length > 0 && React.createElement('div', { className: 'emp-pagination' },
        React.createElement('span', { className: 'emp-pagination-info' },
          'Showing ', ((page - 1) * 10) + 1, ' to ', Math.min(page * 10, total), ' of ', total, ' results'
        ),
        React.createElement('div', { className: 'emp-pagination-controls' },
          React.createElement('button', {
            className: 'emp-page-btn', disabled: page <= 1,
            onClick: function () { setPage(function (p) { return Math.max(1, p - 1); }); },
          }, '\u2039'),
          Array.from({ length: pages }, function (_, i) { return i + 1; }).slice(0, 5).map(function (p) {
            return React.createElement('button', {
              key: p, className: 'emp-page-btn' + (p === page ? ' is-active' : ''),
              onClick: function () { setPage(p); },
            }, p);
          }),
          React.createElement('button', {
            className: 'emp-page-btn', disabled: page >= pages,
            onClick: function () { setPage(function (p) { return Math.min(pages, p + 1); }); },
          }, '\u203A')
        )
      )
    )
  );
};

export default Employees;